import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Tooltip
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaSearch } from 'react-icons/fa';
import { Container, Header, HeaderLeft, BackButton, Title, Content, ControlsContainer } from '../../common/styles';
import {
  MainContainer,
  SkillsGrid,
  SkillCard,
  SkillCardContent,
  SkillTitle,
  EmployeeBadge,
  ActionButtons,
  UsageBadgesContainer,
  UsageBadge,
  CreatedAtText
} from './styles';
import { COLORS } from '../../../constants/radar';
import { useAlert } from '../../../contexts/AlertContext';
import { cvManagementApi } from '../../../services/api';
import RolesSelection from '../../ProjectsManagement/ProjectForm/components/Employees/components/EmployeeTransfer/components/RolesSelection';
import { ROLE_TYPES } from '../../../constants/roleTypes';
import { ORG_UNIT_TYPES } from '../../../constants/radar';
import { getRelativeTime } from '../../../utils/dateUtils';
import Modal from '../../common/Modal';
import LoadingSpinner from '../../common/LoadingSpinner';

const CustomClassificationsReconciliation = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [customClassifications, setCustomClassifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentClassification, setCurrentClassification] = useState({ id: null, name: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showControls, setShowControls] = useState(true);

  // States for position/grade/roles selection
  const [selectedPositionForRoles, setSelectedPositionForRoles] = useState(null);
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedTechnicalRoles, setSelectedTechnicalRoles] = useState([]);
  const [selectedOrganizationalRoles, setSelectedOrganizationalRoles] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);

  // Load custom classifications on component mount
  useEffect(() => {
    fetchCustomClassifications();
  }, []);

  const fetchCustomClassifications = async () => {
    try {
      setLoading(true);
      const response = await cvManagementApi.getCustomClassifications();
      setCustomClassifications(response || []);
    } catch (error) {
      console.error('Error fetching custom classifications:', error);
      showAlert('Failed to load custom classifications', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (classification = null) => {
    if (classification) {
      setCurrentClassification({
        id: classification.id,
        name: classification.name
      });
      // Reset selections
      setSelectedPositionForRoles(null);
      setSelectedFunctionalRoles([]);
      setSelectedTechnicalRoles([]);
      setSelectedOrganizationalRoles([]);
      setSelectedGrade(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPositionForRoles(null);
    setSelectedFunctionalRoles([]);
    setSelectedTechnicalRoles([]);
    setSelectedOrganizationalRoles([]);
    setSelectedGrade(null);
  };

  const handleReconcileClassification = async () => {
    if (!selectedPositionForRoles) {
      showAlert('Please select a position', 'error');
      return;
    }

    if (!selectedGrade) {
      showAlert('Please select a grade', 'error');
      return;
    }

    try {
      // Prepare the reconciliation data
      const reconciliationData = {
        position: {
          id: selectedPositionForRoles.id,
          name: selectedPositionForRoles.label || ''
        },
        grade: {
          id: selectedGrade.id,
          name: selectedGrade.label || ''
        },
        roles: [
          // Map functional roles
          ...selectedFunctionalRoles
            .filter(roleId => selectedPositionForRoles?.roles?.some(r => r.id === roleId && r.type === ROLE_TYPES.FUNCTIONAL))
            .map(roleId => {
              const role = selectedPositionForRoles?.roles?.find(r => r.id === roleId);
              return {
                id: roleId,
                name: role?.label || '',
                type: ROLE_TYPES.FUNCTIONAL
              }
            }),
          // Map technical roles
          ...selectedTechnicalRoles
            .filter(roleId => selectedPositionForRoles?.roles?.some(r => r.id === roleId && r.type === ROLE_TYPES.TECHNICAL))
            .map(roleId => {
              const role = selectedPositionForRoles?.roles?.find(r => r.id === roleId);
              return {
                id: roleId,
                name: role?.label || '',
                type: ROLE_TYPES.TECHNICAL
              }
            }),
          // Include organizational roles
          ...selectedOrganizationalRoles
            .filter(roleId => selectedPositionForRoles?.roles?.some(r => r.id === roleId && r.type === ROLE_TYPES.ORGANIZATIONAL))
            .map(roleId => {
              const role = selectedPositionForRoles?.roles?.find(r => r.id === roleId);
              return {
                id: roleId,
                name: role?.label || '',
                type: ROLE_TYPES.ORGANIZATIONAL
              }
            })
        ]
      };

      // Call the API to reconcile the custom classification
      await cvManagementApi.reconcileCustomClassification(
        currentClassification.id,
        reconciliationData
      );

      showAlert('Classification reconciled successfully', 'success');

      // Refresh classifications list
      await fetchCustomClassifications();
      handleCloseDialog();
    } catch (error) {
      console.error('Error reconciling classification:', error);
      showAlert(`Failed to reconcile classification: ${error.message}`, 'error');
    }
  };

  // Filter classifications based on search term
  const filteredClassifications = customClassifications.filter(classification =>
    classification.name.toLowerCase().includes((searchTerm || '').toLowerCase()) ||
    (classification.cv && classification.cv.fullName.toLowerCase().includes((searchTerm || '').toLowerCase()))
  );

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/cv-configuration')}>
            <FaArrowLeft /> Back to CV Configuration
          </BackButton>
          <Title>Custom Classifications Reconciliation</Title>
        </HeaderLeft>
      </Header>

      <Content>
        <ControlsContainer $show={showControls} style={{ marginBottom: '20px' }}>
          <Box sx={{
            display: 'flex',
            gap: '16px',
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'flex-start',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', flex: 1 }}>
              <TextField
                placeholder="Search Classifications"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaSearch style={{ color: '#6B7280' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  maxWidth: '500px',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    height: '40px',
                    fontSize: '14px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2563eb',
                    }
                  },
                  '& .MuiInputBase-input': {
                    padding: '8px 14px',
                  }
                }}
              />
            </Box>
          </Box>
        </ControlsContainer>

        <MainContainer>
          {!loading && (
            <div style={{
              color: '#64748B',
              fontSize: '14px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <span style={{ fontWeight: 600 }}>
                {filteredClassifications.length}
              </span>
              {filteredClassifications.length === 1 ? 'classification' : 'classifications'} found
            </div>
          )}

          {loading ? (
            <LoadingSpinner message="Loading classifications..." />
          ) : filteredClassifications.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px dashed #dee2e6'
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: '#6c757d',
                  marginBottom: '8px',
                  fontWeight: 500
                }}
              >
                No classifications found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#adb5bd',
                  maxWidth: '400px'
                }}
              >
                {searchTerm ?
                  "No classifications match your search criteria. Try adjusting your search term." :
                  "No custom classifications have been added yet."}
              </Typography>
            </Box>
          ) : (
            <SkillsGrid>
              {filteredClassifications.map((classification) => (
                <SkillCard key={classification.id}>
                  <SkillCardContent>
                    <SkillTitle>
                      {classification.name}
                      <UsageBadgesContainer>
                        {classification.usedIn?.professionalDetails && (
                          <UsageBadge>
                            <AccountBoxIcon /> Professional Details
                          </UsageBadge>
                        )}
                        {classification.usedIn?.employmentHistories && (
                          <UsageBadge>
                            <WorkIcon /> Employment History
                          </UsageBadge>
                        )}
                      </UsageBadgesContainer>
                    </SkillTitle>

                    {classification.cv && (
                      <EmployeeBadge>
                        <PersonIcon fontSize="small" /> {classification.cv.fullName}
                      </EmployeeBadge>
                    )}

                    {classification.createdAt && (
                      <CreatedAtText>
                        <AccessTimeIcon /> Added {getRelativeTime(classification.createdAt)}
                      </CreatedAtText>
                    )}

                    <ActionButtons>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(classification)}
                        title="Reconcile with position, grade and roles"
                      >
                        <LinkIcon />
                      </IconButton>
                    </ActionButtons>
                  </SkillCardContent>
                </SkillCard>
              ))}
            </SkillsGrid>
          )}
        </MainContainer>
      </Content>

      {/* Reconcile Classification Dialog */}
      <Modal
        isOpen={openDialog}
        onClose={handleCloseDialog}
        title="Reconcile Custom Classification"
        onSubmit={handleReconcileClassification}
        submitText="Reconcile"
        width="1200px"
        submitButtonProps={{
          sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
        }}
      >
        <DialogContentText sx={{ mb: 3 }}>
          Reconcile the custom classification <strong>"{currentClassification.name}"</strong> with a standard position, grade, and roles combination.
        </DialogContentText>

        <RolesSelection
          selectedPositionForRoles={selectedPositionForRoles}
          setSelectedPositionForRoles={setSelectedPositionForRoles}
          selectedFunctionalRoles={selectedFunctionalRoles}
          setSelectedFunctionalRoles={setSelectedFunctionalRoles}
          selectedTechnicalRoles={selectedTechnicalRoles}
          setSelectedTechnicalRoles={setSelectedTechnicalRoles}
          selectedOrganizationalRoles={selectedOrganizationalRoles}
          setSelectedOrganizationalRoles={setSelectedOrganizationalRoles}
          hidePositionInfo={true}
          allowedOrgUnitTypes={[ORG_UNIT_TYPES.DELIVERY]}
          showGrades={true}
          selectedGrade={selectedGrade}
          setSelectedGrade={(grade) => {
            setSelectedGrade({
              id: grade.id,
              label: grade.label
            });
          }}
        />
      </Modal>
    </Container>
  );
};

export default CustomClassificationsReconciliation;
